<template>
  <div class="author-registration-form">
    <form @submit.prevent="register">
      <h3>Pendaftaran Penulis</h3>
      <p class="form-description">
        Bergabunglah dengan komunitas penulis HobiBaca dan bagikan karya Anda dengan ribuan pembaca.
      </p>
      
      <!-- Account Information -->
      <div class="form-section">
        <h4>Informasi Akun</h4>
        <div class="form-group">
          <label for="username">Username <span class="required">*</span></label>
          <input 
            type="text" 
            id="username" 
            v-model="formData.username" 
            @blur="validateField('username')"
            :class="{'error-input': usernameError}"
            required
          />
          <span v-if="usernameError" class="error-message">{{ usernameError }}</span>
          <span v-if="usernameAvailable" class="success-message">Username tersedia!</span>
        </div>
        <div class="form-group">
          <label for="email">Email <span class="required">*</span></label>
          <input 
            type="email" 
            id="email" 
            v-model="formData.email" 
            @blur="validateField('email')" 
            :class="{'error-input': emailError}"
            required 
          />
          <span v-if="emailError" class="error-message">{{ emailError }}</span>
          <span v-if="emailAvailable" class="success-message">Email tersedia!</span>
        </div>
        <div class="form-group">
          <label for="password">Password <span class="required">*</span></label>
          <div class="password-input">
            <input 
              :type="showPassword ? 'text' : 'password'" 
              id="password" 
              v-model="formData.password" 
              @blur="validatePassword"
              :class="{'error-input': passwordError}"
              required 
            />
            <button 
              type="button" 
              class="toggle-password" 
              @click="showPassword = !showPassword"
            >
              <font-awesome-icon :icon="['fas', showPassword ? 'eye-slash' : 'eye']" />
            </button>
          </div>
          <span v-if="passwordError" class="error-message">{{ passwordError }}</span>
          <span class="form-hint">Minimal 8 karakter</span>
        </div>
        <div class="form-group">
          <label for="confirmPassword">Konfirmasi Password <span class="required">*</span></label>
          <div class="password-input">
            <input 
              :type="showConfirmPassword ? 'text' : 'password'" 
              id="confirmPassword" 
              v-model="formData.confirmPassword" 
              @input="validatePasswordMatch"
              :class="{'error-input': confirmPasswordError}"
              required 
            />
            <button 
              type="button" 
              class="toggle-password" 
              @click="showConfirmPassword = !showConfirmPassword"
            >
              <font-awesome-icon :icon="['fas', showConfirmPassword ? 'eye-slash' : 'eye']" />
            </button>
          </div>
          <span v-if="confirmPasswordError" class="error-message">{{ confirmPasswordError }}</span>
        </div>
      </div>
      
      <!-- Personal Information -->
      <div class="form-section">
        <h4>Informasi Pribadi</h4>
        <div class="form-group">
          <label for="fullName">Nama Lengkap <span class="required">*</span></label>
          <input type="text" id="fullName" v-model="formData.fullName" required />
        </div>
        <div class="form-group">
          <label for="phone">Nomor Telepon <span class="required">*</span></label>
          <input 
            type="tel" 
            id="phone" 
            v-model="formData.phone" 
            @blur="validatePhone"
            :class="{'error-input': phoneError}"
            required 
          />
          <span v-if="phoneError" class="error-message">{{ phoneError }}</span>
          <span class="form-hint">Contoh: 08123456789</span>
        </div>
        <div class="form-group">
          <label for="bio">Biografi <span class="form-label-optional">(opsional)</span></label>
          <textarea 
            id="bio" 
            v-model="formData.bio" 
            rows="4" 
            placeholder="Ceritakan tentang diri Anda sebagai penulis..."
          ></textarea>
        </div>
      </div>
      
      <!-- Professional Information -->
      <div class="form-section">
        <h4>Informasi Profesional</h4>
        <div class="form-group">
          <label>Genre Penulisan <span class="required">*</span></label>
          <div class="checkbox-group">
            <div 
              v-for="(genre, index) in genres" 
              :key="index" 
              class="checkbox-item"
            >
              <input 
                type="checkbox" 
                :id="'genre-' + index" 
                :value="genre.value" 
                v-model="formData.selectedGenres"
                @change="validateGenres"
              />
              <label :for="'genre-' + index">{{ genre.label }}</label>
            </div>
          </div>
          <span v-if="genreError" class="error-message">{{ genreError }}</span>
        </div>
        <div class="form-group">
          <label for="writingExperience">Pengalaman Menulis <span class="required">*</span></label>
          <select id="writingExperience" v-model="formData.writingExperience" required>
            <option value="" disabled selected>Pilih pengalaman Anda</option>
            <option value="beginner">Pemula (0-1 tahun)</option>
            <option value="intermediate">Menengah (1-3 tahun)</option>
            <option value="experienced">Berpengalaman (3-5 tahun)</option>
            <option value="professional">Profesional (5+ tahun)</option>
          </select>
        </div>
        <div class="form-group">
          <label for="publishedWorks">Karya yang Pernah Diterbitkan <span class="form-label-optional">(opsional)</span></label>
          <textarea 
            id="publishedWorks" 
            v-model="formData.publishedWorks" 
            rows="3"
            placeholder="Sebutkan karya Anda yang pernah diterbitkan (jika ada)..."
          ></textarea>
        </div>
      </div>
      
      <!-- Social Media and Links -->
      <div class="form-section">
        <h4>Media Sosial & Website</h4>
        <p class="form-description small">Opsional - Bantu pembaca menemukan Anda di platform lain</p>
        <div class="form-group">
          <label for="website">Website/Blog</label>
          <input 
            type="url" 
            id="website" 
            v-model="formData.website" 
            placeholder="https://"
            @blur="validateUrl"
            :class="{'error-input': websiteError}"
          />
          <span v-if="websiteError" class="error-message">{{ websiteError }}</span>
        </div>
        <div class="form-group">
          <label for="instagram">Instagram</label>
          <div class="input-with-prefix">
            <span class="input-prefix">@</span>
            <input
              type="text"
              id="instagram"
              v-model="formData.instagram"
              placeholder="username"
            />
          </div>
        </div>
        <div class="form-group">
          <label for="twitter">Twitter</label>
          <div class="input-with-prefix">
            <span class="input-prefix">@</span>
            <input
              type="text"
              id="twitter"
              v-model="formData.twitter"
              placeholder="username"
            />
          </div>
        </div>
      </div>
      
      <!-- Terms and Conditions -->
      <div class="form-section">
        <div class="form-group checkbox-group">
          <div class="checkbox-item full-width">
            <input 
              type="checkbox" 
              id="terms" 
              v-model="formData.agreeToTerms" 
              required
              :class="{'error-input': termsError}"
            />
            <label for="terms">
              Saya setuju dengan <a href="/ketentuan-penulis" target="_blank">Ketentuan Penulisan</a> dan <a href="/privacy" target="_blank">Kebijakan Privasi</a> HobiBaca
            </label>
          </div>
          <span v-if="termsError" class="error-message">{{ termsError }}</span>
        </div>
      </div>
      
      <button type="submit" :class="['btn-primary', {'btn-loading': isSubmitting}]" :disabled="isSubmitting || isFormInvalid">
        <span v-if="isSubmitting">
          <font-awesome-icon :icon="['fas', 'spinner']" spin />
          Memproses...
        </span>
        <span v-else>Daftar Sebagai Penulis</span>
      </button>
    </form>
    <p v-if="errorMessage" class="error-message form-error">{{ errorMessage }}</p>
    <p v-if="successMessage" class="success-message form-success">{{ successMessage }}</p>
  </div>
</template>

<script>
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';

export default {
  name: 'AuthorRegistrationForm',
  data() {
    return {
      formData: {
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        fullName: '',
        phone: '',
        bio: '',
        selectedGenres: [],
        writingExperience: '',
        publishedWorks: '',
        website: '',
        instagram: '',
        twitter: '',
        agreeToTerms: false
      },
      genres: [
        { label: 'Fiksi', value: 'fiction' },
        { label: 'Non-Fiksi', value: 'non-fiction' },
        { label: 'Puisi', value: 'poetry' },
        { label: 'Biografi', value: 'biography' },
        { label: 'Anak-anak', value: 'children' },
        { label: 'Petualangan', value: 'adventure' },
        { label: 'Fantasi', value: 'fantasy' },
        { label: 'Romantis', value: 'romance' },
        { label: 'Misteri', value: 'mystery' },
        { label: 'Horor', value: 'horror' },
        { label: 'Pengembangan Diri', value: 'self-help' },
        { label: 'Sejarah', value: 'history' }
      ],
      isSubmitting: false,
      errorMessage: '',
      successMessage: '',
      // Field validation states
      usernameError: '',
      usernameAvailable: false,
      emailError: '',
      emailAvailable: false,
      passwordError: '',
      confirmPasswordError: '',
      phoneError: '',
      genreError: '',
      websiteError: '',
      termsError: '',
      // UI controls
      showPassword: false,
      showConfirmPassword: false,
      // Debounce timers
      usernameTimer: null,
      emailTimer: null,
      // Validation status tracking
      validationInProgress: false
    };
  },
  computed: {
    isFormInvalid() {
      return (
        !this.formData.username ||
        this.usernameError ||
        !this.formData.email ||
        this.emailError ||
        !this.formData.password ||
        this.passwordError ||
        !this.formData.confirmPassword ||
        this.confirmPasswordError ||
        !this.formData.fullName ||
        !this.formData.phone ||
        this.phoneError ||
        this.formData.selectedGenres.length === 0 ||
        !this.formData.writingExperience ||
        !this.formData.agreeToTerms 
        //this.termsError
      );
    }
  },
  methods: {
    // Field validation methods
    validateField(field) {
      if (field === 'username') {
        if (this.usernameTimer) clearTimeout(this.usernameTimer);
        
        if (!this.formData.username) {
          this.usernameError = 'Username harus diisi';
          this.usernameAvailable = false;
          return;
        }
        
        if (this.formData.username.length < 3) {
          this.usernameError = 'Username minimal 3 karakter';
          this.usernameAvailable = false;
          return;
        }
        
        // Check if username contains spaces
        if (/\s/.test(this.formData.username)) {
          this.usernameError = 'Username tidak boleh mengandung spasi';
          this.usernameAvailable = false;
          return;
        }
        
        // Check if username only contains alphanumeric chars and underscores
        if (!/^[a-zA-Z0-9_]+$/.test(this.formData.username)) {
          this.usernameError = 'Username hanya boleh berisi huruf, angka, dan underscore';
          this.usernameAvailable = false;
          return;
        }
        
        this.usernameError = '';
        
        // Debounce to avoid too many requests
        this.usernameTimer = setTimeout(() => {
          this.checkUsernameAvailability();
        }, 500);
      } else if (field === 'email') {
        if (this.emailTimer) clearTimeout(this.emailTimer);
        
        if (!this.formData.email) {
          this.emailError = 'Email harus diisi';
          this.emailAvailable = false;
          return;
        }
        
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.formData.email)) {
          this.emailError = 'Format email tidak valid';
          this.emailAvailable = false;
          return;
        }
        
        this.emailError = '';
        
        // Debounce to avoid too many requests
        this.emailTimer = setTimeout(() => {
          this.checkEmailAvailability();
        }, 500);
      }
    },
    
    async checkUsernameAvailability() {
      try {
        this.usernameAvailable = false;
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/users/validate-field/`,
          {
            params: {
              field: 'username',
              value: this.formData.username
            }
          }
        );
        
        if (response.data.is_taken) {
          this.usernameError = 'Username sudah digunakan, silakan pilih yang lain';
          this.usernameAvailable = false;
        } else {
          this.usernameError = '';
          this.usernameAvailable = true;
        }
      } catch (error) {
        console.error('Error checking username:', error);
        this.usernameError = 'Gagal memeriksa ketersediaan username';
        this.usernameAvailable = false;
      }
    },
    
    async checkEmailAvailability() {
      try {
        this.emailAvailable = false;
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/users/validate-field/`,
          {
            params: {
              field: 'email',
              value: this.formData.email
            }
          }
        );
        
        if (response.data.is_taken) {
          this.emailError = 'Email sudah terdaftar, silakan gunakan email lain';
          this.emailAvailable = false;
        } else {
          this.emailError = '';
          this.emailAvailable = true;
        }
      } catch (error) {
        console.error('Error checking email:', error);
        this.emailError = 'Gagal memeriksa ketersediaan email';
        this.emailAvailable = false;
      }
    },
    
    validatePassword() {
      if (!this.formData.password) {
        this.passwordError = 'Password harus diisi';
        return;
      }
      
      if (this.formData.password.length < 8) {
        this.passwordError = 'Password minimal 8 karakter';
        return;
      }
      
      this.passwordError = '';
      this.validatePasswordMatch();
    },
    
    validatePasswordMatch() {
      if (!this.formData.confirmPassword) {
        this.confirmPasswordError = '';
        return;
      }
      
      if (this.formData.password !== this.formData.confirmPassword) {
        this.confirmPasswordError = 'Password dan konfirmasi password tidak cocok';
        return;
      }
      
      this.confirmPasswordError = '';
    },
    
    validatePhone() {
      if (!this.formData.phone) {
        this.phoneError = 'Nomor telepon harus diisi';
        return;
      }
      
      // Remove non-numeric characters for validation
      const phoneDigits = this.formData.phone.replace(/\D/g, '');
      
      if (phoneDigits.length < 10 || phoneDigits.length > 13) {
        this.phoneError = 'Nomor telepon harus 10-13 digit';
        return;
      }
      
      this.phoneError = '';
    },
    
    validateGenres() {
      if (this.formData.selectedGenres.length === 0) {
        this.genreError = 'Pilih setidaknya satu genre penulisan';
        return;
      }
      
      this.genreError = '';
    },
    
    validateUrl() {
      if (!this.formData.website) {
        this.websiteError = '';
        return;
      }
      
      try {
        const url = new URL(this.formData.website);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
          this.websiteError = 'URL harus dimulai dengan http:// atau https://';
          return;
        }
        this.websiteError = '';
      } catch (e) {
        this.websiteError = 'Format URL tidak valid';
      }
    },
    
    validateTerms() {
      if (!this.formData.agreeToTerms) {
        this.termsError = 'Anda harus menyetujui ketentuan dan kebijakan privasi';
        return;
      }
      
      this.termsError = '';
    },
    
    validateForm() {
      // Reset all validation states
      this.errorMessage = '';
      this.successMessage = '';
      
      // Validate username
      this.validateField('username');
      
      // Validate email
      this.validateField('email');
      
      // Validate password
      this.validatePassword();
      
      // Validate password match
      this.validatePasswordMatch();
      
      // Validate phone
      this.validatePhone();
      
      // Validate genres
      this.validateGenres();
      
      // Validate website if entered
      if (this.formData.website) {
        this.validateUrl();
      }
      
      // Validate terms
      if (!this.formData.agreeToTerms) {
        this.termsError = 'Anda harus menyetujui ketentuan dan kebijakan privasi';
      } else {
        this.termsError = '';
      }
      
      // Return validation result
      return !this.isFormInvalid;
    },
    
    async register() {
      // Validate all form fields
      if (!this.validateForm()) {
        this.errorMessage = 'Mohon perbaiki semua error pada form sebelum melanjutkan.';
        return;
      }
      
      // Disable form submission during validation check
      if (this.validationInProgress) {
        return;
      }
      
      this.validationInProgress = true;
      
      // Recheck username and email availability
      try {
        const [usernameResponse, emailResponse] = await Promise.all([
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/users/validate-field/`, {
            params: { field: 'username', value: this.formData.username }
          }),
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/users/validate-field/`, {
            params: { field: 'email', value: this.formData.email }
          })
        ]);
        
        if (usernameResponse.data.is_taken) {
          this.usernameError = 'Username sudah digunakan, silakan pilih yang lain';
          this.validationInProgress = false;
          return;
        }
        
        if (emailResponse.data.is_taken) {
          this.emailError = 'Email sudah terdaftar, silakan gunakan email lain';
          this.validationInProgress = false;
          return;
        }
      } catch (error) {
        this.errorMessage = 'Gagal memvalidasi data. Silakan coba lagi.';
        this.validationInProgress = false;
        return;
      }
      
      this.isSubmitting = true;
      this.errorMessage = '';
      
      try {
        // 1. Register user with writer role
        const registerData = {
          username: this.formData.username,
          email: this.formData.email,
          password: this.formData.password,
          role: 'writer'  // Set role to writer
        };

        const registerResponse = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/users/`,
          registerData
        );

        if (registerResponse.data) {
          // 2. Auto login after registration
          const loginResponse = await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/api/token/`,
            {
              username: this.formData.username,
              password: this.formData.password
            }
          );

          if (loginResponse.data && loginResponse.data.access) {
            // Use auth store instead of direct localStorage
            const authStore = useAuthStore();
            authStore.setTokens(loginResponse.data.access, loginResponse.data.refresh);
            authStore.username = this.formData.username;
            authStore.role = loginResponse.data.role || 'writer';
            authStore.avatar = loginResponse.data.avatar || '';
            authStore.syncToLocalStorage();
            
            // 3. Create author profile with additional information
            const authorProfileData = {
              name: this.formData.fullName,
              phone: this.formData.phone,
              bio: this.formData.bio,
              genres: this.formData.selectedGenres,
              experience_level: this.formData.writingExperience,
              published_works: this.formData.publishedWorks,
              website: this.formData.website,
              instagram: this.formData.instagram ? this.formData.instagram.replace('@', '') : '',
              twitter: this.formData.twitter ? this.formData.twitter.replace('@', '') : ''
            };

            try {
              await axios.post(
                `${process.env.VUE_APP_API_BASE_URL}/api/author-profiles/`,
                authorProfileData,
                {
                  headers: {
                    Authorization: `Bearer ${loginResponse.data.access}`
                  }
                }
              );
  
              // Emit event for parent component
              this.$emit('register-success', { token: loginResponse.data.access });
              
              this.successMessage = 'Pendaftaran berhasil! Anda akan dialihkan ke dashboard penulis...';
              
              // Reset form
              this.resetForm();
  
              // Redirect to writer dashboard
              setTimeout(() => {
                this.$router.push('/my-books');
              }, 2000);
            } catch (profileError) {
              console.error('Error creating author profile:', profileError);
              
              // Although user is registered, profile creation failed
              this.errorMessage = 'Pendaftaran akun berhasil, tetapi profil penulis gagal dibuat. Silakan coba lagi nanti.';
              
              // Redirect to home page instead
              setTimeout(() => {
                this.$router.push('/');
              }, 3000);
            }
          } else {
            this.errorMessage = 'Login otomatis gagal setelah pendaftaran. Silakan coba login manual.';
          }
        } else {
          this.errorMessage = 'Pendaftaran gagal: Respons kosong dari server.';
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isSubmitting = false;
        this.validationInProgress = false;
      }
    },
    
    resetForm() {
      this.formData = {
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        fullName: '',
        phone: '',
        bio: '',
        selectedGenres: [],
        writingExperience: '',
        publishedWorks: '',
        website: '',
        instagram: '',
        twitter: '',
        agreeToTerms: false
      };
      
      // Reset validation states
      this.usernameError = '';
      this.usernameAvailable = false;
      this.emailError = '';
      this.emailAvailable = false;
      this.passwordError = '';
      this.confirmPasswordError = '';
      this.phoneError = '';
      this.genreError = '';
      this.websiteError = '';
      this.termsError = '';
      this.errorMessage = '';
      this.successMessage = '';
    },
    
    handleError(error) {
      if (error.response?.data) {
        if (error.response.data.username) {
          this.usernameError = error.response.data.username[0] || 'Username sudah digunakan';
          this.errorMessage = 'Terjadi kesalahan pada field username.';
        } else if (error.response.data.email) {
          this.emailError = error.response.data.email[0] || 'Email sudah terdaftar';
          this.errorMessage = 'Terjadi kesalahan pada field email.';
        } else if (error.response.data.password) {
          this.passwordError = error.response.data.password[0];
          this.errorMessage = 'Terjadi kesalahan pada field password.';
        } else if (error.response.status === 405) {
          this.errorMessage = 'Server tidak memperbolehkan metode ini. Silakan coba lagi nanti.';
        } else {
          this.errorMessage = error.response.data.message || error.response.data.detail || 'Terjadi kesalahan saat mendaftar';
        }
      } else if (error.request) {
        // Request was made but no response received
        this.errorMessage = 'Tidak dapat terhubung ke server. Periksa koneksi internet Anda.';
      } else {
        // Something happened in setting up the request
        this.errorMessage = error.message || 'Terjadi kesalahan pada sistem';
      }
      this.successMessage = '';
    }
  }
};
</script>

<style scoped>
.author-registration-form {
  max-width: 600px;
  margin: 0 auto;
}

form {
  background-color: #fff;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

h3 {
  color: #333;
  font-size: 24px;
  text-align: center;
  margin-bottom: 5px;
}

.form-description {
  text-align: center;
  color: #666;
  margin-bottom: 25px;
}

.form-description.small {
  font-size: 0.9em;
  margin-top: -10px;
  margin-bottom: 15px;
}

.form-section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.form-section h4 {
  font-size: 18px;
  color: #488e71;
  margin-bottom: 15px;
}

.form-group {
  margin-bottom: 18px;
}

label {
  display: block;
  font-weight: 600;
  margin-bottom: 6px;
  color: #333;
}

.required {
  color: #e74c3c;
  font-weight: bold;
}

.form-label-optional {
  font-weight: normal;
  font-style: italic;
  color: #888;
  font-size: 0.9em;
}

input, select, textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 15px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

input:focus, select:focus, textarea:focus {
  outline: none;
  border-color: #488e71;
  box-shadow: 0 0 0 2px rgba(72, 142, 113, 0.1);
}

.error-input {
  border-color: #e74c3c !important;
  background-color: rgba(231, 76, 60, 0.05);
}

.form-hint {
  font-size: 0.8em;
  color: #888;
  margin-top: 4px;
  display: block;
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  width: calc(50% - 5px);
}

.checkbox-item.full-width {
  width: 100%;
}

.checkbox-item input[type="checkbox"] {
  width: auto;
  margin-right: 8px;
}

.password-input {
  position: relative;
  display: flex;
  align-items: center;
}

.toggle-password {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: #777;
  cursor: pointer;
  padding: 0;
  font-size: 16px;
}

.input-with-prefix {
  display: flex;
  align-items: center;
}

.input-prefix {
  background-color: #f5f5f5;
  padding: 12px;
  border: 1px solid #ddd;
  border-right: none;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
  color: #666;
}

.input-with-prefix input {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.btn-primary {
  width: 100%;
  padding: 14px;
  background-color: #488e71;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: background-color 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
}

.btn-primary:hover:not(:disabled) {
  background-color: #3a7259;
}

.btn-primary:disabled {
  background-color: #93c3b1;
  cursor: not-allowed;
}

.btn-loading {
  position: relative;
}

.btn-loading svg {
  margin-right: 8px;
}

.error-message {
  color: #e74c3c;
  font-size: 0.9em;
  margin-top: 5px;
  display: block;
}

.success-message {
  color: #27ae60;
  font-size: 0.9em;
  margin-top: 5px;
  display: block;
}

.form-error, .form-success {
  text-align: center;
  margin-top: 15px;
  padding: 10px;
  border-radius: 5px;
  font-weight: 500;
}

.form-error {
  background-color: rgba(231, 76, 60, 0.1);
}

.form-success {
  background-color: rgba(39, 174, 96, 0.1);
}

a {
  color: #488e71;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

/* Responsive styles */
@media (max-width: 768px) {
  .checkbox-item {
    width: 100%;
  }
  
  form {
    padding: 20px;
  }
}
</style>